import React, { memo, useCallback } from 'react';
import { css } from '@emotion/css';
import { Modal, ModalBackdrop, ModalContainer } from 'is24-corecss';
import { useSearchParams } from 'react-router-dom';
import WohnenPlusUpsellContent from './WohnenPlusUpsellContent';
import { campaigns } from '@/components/common/campaigns';
import { useIntl } from '@/i18n/hooks/useIntl';
import { useBodyScroll } from '@/customHooks/useBodyScroll.hook';
import { WOHNEN_UPSELL } from '@/pages/tenant/components/WohnenUpsellModal/wohnenUpsell.config';
import useWohnenPlusUpsellContentExperimentConfig from '@/customHooks/useWohnenPlusUpsellContentExperimentConfig.hook';
import { useModalFocus } from '@/customHooks/useModalFocus.hook';
import { useExperimentLanguage } from '@/customHooks/useExperimentLanguage.hook';

const styles = {
  modalBackDrop: css`
    z-index: 1000;
    /* iOS webview stacking context fixes */
    -webkit-transform: translateZ(0);
    transform: translateZ(0);
    will-change: transform;
  `,
  modalContentWrapper: css`
    max-height: inherit !important;
    overflow: hidden !important;
    flex-flow: column;
    height: 100%;
    @media (min-width: 668px) {
      max-height: calc(100vh - 200px) !important;
    }
  `,
  modalContent: css`
    width: 628px !important;
    @media (max-width: 668px) {
      width: inherit !important;
    }
    > [class*='Modal_modal-title-wrapper'] {
      border: 0;
    }
    > [class*='Modal_modal-footer'] {
      border: 0;
    }
  `,
} as const;

interface WohnenPlusUpsellModalProps {
  onClose: () => void;
}

const WohnenPlusUpsellModal: React.FC<WohnenPlusUpsellModalProps> = ({ onClose }) => {
  const { t } = useIntl();
  const [searchParams] = useSearchParams();
  const articleId = searchParams.get('articleId');
  const selectedArticle = articleId || WOHNEN_UPSELL.ARTICLE_IDS.MONTHLY;
  const { configurationIds, campaignUrls } = useWohnenPlusUpsellContentExperimentConfig();
  const paymentFrequency =
    WOHNEN_UPSELL.PAYMENT_FREQUENCY[selectedArticle as keyof typeof WOHNEN_UPSELL.PAYMENT_FREQUENCY];
  const configId = configurationIds[paymentFrequency];
  const { langKey } = useExperimentLanguage();

  useBodyScroll(true);
  useModalFocus(true);

  const handleContinueToPayment = useCallback(() => {
    window.location.href = `${campaigns.UPSELL_MODAL_BUY_NOW}${selectedArticle}${campaignUrls.BUY_NOW}&selectedArticle=${selectedArticle}&articleConfigurationId=${configId}&moveToPayment=true&livingUpsell=true`;
  }, [selectedArticle, configId]);

  const handleLearnMore = useCallback(() => {
    window.location.href = `${campaignUrls.LEARN_MORE}&selectedArticle=${selectedArticle}&articleConfigurationId=${configId}`;
  }, [selectedArticle, configId]);
  return (
    <ModalBackdrop visible={true} onClose={onClose} className={css(styles.modalBackDrop)}>
      <ModalContainer
        className={`modal-container ${css(styles.modalContent)}`}
        contentWrapperClassName={`absolute-reference display-flex ${css(styles.modalContentWrapper)}`}
        visible={true}
        modalTitle={' '}
        onClose={onClose}
        primaryFooterBtn={
          <Modal.FooterPrimaryBtn
            label={t({ id: `welcomeModal.wohnenUpsell.${langKey}.buttonText.continueToPayment` })}
            onClick={handleContinueToPayment}
          />
        }
        secondaryFooterBtn={
          <Modal.FooterSecondaryBtn
            label={t({ id: `welcomeModal.wohnenUpsell.${langKey}.buttonText.otherOptions` })}
            onClick={handleLearnMore}
          />
        }
      >
        <WohnenPlusUpsellContent />
      </ModalContainer>
    </ModalBackdrop>
  );
};

export default memo(WohnenPlusUpsellModal);
